/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { styles } from '../ReportsScreen.styles';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { CatalogPagination } from '../../../shared/ui/CatalogPagination';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { ReportsSalesByProductRows } from './ReportsSalesByProductRows';
import { ReportsDenseSectionShell } from './ReportsDenseSectionShell';
import { ticketColors } from '../../../shared/theme/ticketColors';

/**
 * Listado «Ventas por ítem»: orden en tabla/tarjetas (filtros viven arriba en Ventas junto al período).
 */
export function ReportsSalesByProductSection({
  vm,
  dateRangeLabel,
  periodDataLoading,
  daysInRange,
}: {
  vm: ReportsScreenViewModel;
  dateRangeLabel: string;
  periodDataLoading: boolean;
  daysInRange: number;
}) {
  const pageSize = vm.salesByProductPageSize;
  const hasMore = (vm.salesByProductPage + 1) * pageSize < vm.salesByProductTotalCount;
  const showRemoteHint = vm.salesByProductSource === null && !vm.salesByProductLoading;

  return (
    <ReportsDenseSectionShell accessibilityLabel={`Ventas por ítem · ${dateRangeLabel}`}>
      {vm.salesByProductLoading ? (
        <View style={styles.ventasPeriodUpdatingRow} accessibilityLiveRegion="polite" accessibilityLabel="Cargando ventas por ítem">
          <ActivityIndicator size="small" color={ticketColors.ink2} accessibilityLabel="Cargando" />
          <Text style={styles.ventasPeriodUpdatingText}>Cargando ventas por ítem…</Text>
        </View>
      ) : showRemoteHint ? (
        <Text style={[styles.caption, { color: colors.warning }]}>
          Sin datos del servidor. Conectate y recargá para ver el listado del período.
        </Text>
      ) : vm.salesByProductTotalCount === 0 ? (
        <Text style={styles.caption}>No hay ítems en este período con esa búsqueda y esos filtros.</Text>
      ) : (
        <>
          <ReportsSalesByProductRows
            rows={vm.salesByProductRows}
            pageIndex={vm.salesByProductPage}
            pageSize={pageSize}
            sumLineTotal={vm.salesByProductSumLineTotal}
            sumQuantity={vm.salesByProductSumQuantity}
            daysInRange={daysInRange}
            sort={vm.salesByProductSort}
            onSortChange={vm.setSalesByProductSort}
            filterCategory={vm.salesByProductCategory}
            abcCumulativeBefore={vm.salesByProductAbcCumulativeBefore}
          />

          <CatalogPagination
            variant="default"
            placement="footer"
            catalogPageIndex={vm.salesByProductPage}
            pageSize={pageSize}
            catalogTotalCount={vm.salesByProductTotalCount}
            hasMore={hasMore}
            productsLoading={vm.salesByProductLoading}
            productCount={vm.salesByProductRows.length}
            listNouns={{ singular: 'ítem', plural: 'ítems' }}
            alwaysShowPagination={vm.salesByProductTotalCount > pageSize}
            onPreviousPage={vm.goToPrevSalesByProductPage}
            onNextPage={vm.goToNextSalesByProductPage}
          />
        </>
      )}

      <TouchableOpacity
        style={[
          styles.secondaryButton,
          (periodDataLoading || vm.salesByProductLoading || showRemoteHint) && styles.disabled,
          { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, alignSelf: 'flex-start', marginTop: spacing.md },
        ]}
        onPress={vm.handleShareSalesByProduct}
        disabled={periodDataLoading || vm.salesByProductLoading || showRemoteHint}
        accessibilityLabel="Exportar ventas por ítem a CSV"
        accessibilityRole="button"
      >
        <Ionicons name="download-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.secondaryButtonText}>Exportar CSV</Text>
      </TouchableOpacity>
    </ReportsDenseSectionShell>
  );
}
